import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSubscription } from '../../hooks/useSubscription';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiUsers, FiCalendar, FiTrendingUp, FiDownload, FiFilter } = FiIcons;

const PaymentManagement = () => {
  const { 
    subscriptionStats, 
    paymentHistory, 
    trialUsers, 
    loading,
    extendTrial,
    refundPayment
  } = useSubscription();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');

  const stats = [
    {
      title: 'Total Revenue',
      value: `AED ${subscriptionStats?.totalRevenue || 0}`,
      change: '+12.5%',
      color: 'green',
      icon: FiDollarSign
    },
    {
      title: 'Paid Users',
      value: subscriptionStats?.paidUsers || 0,
      change: '+8.2%',
      color: 'blue',
      icon: FiUsers
    },
    {
      title: 'Trial Users',
      value: subscriptionStats?.trialUsers || 0,
      change: '-2.1%',
      color: 'yellow',
      icon: FiCalendar
    },
    {
      title: 'Conversion Rate',
      value: `${subscriptionStats?.conversionRate || 0}%`,
      change: '+5.3%',
      color: 'purple',
      icon: FiTrendingUp
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'trial':
        return 'text-yellow-600 bg-yellow-50';
      case 'expired':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleExtendTrial = async (userId, days) => {
    try {
      await extendTrial(userId, days);
    } catch (error) {
      console.error('Error extending trial:', error);
    }
  };

  const handleRefund = async (paymentId) => {
    if (window.confirm('Are you sure you want to process this refund?')) {
      try {
        await refundPayment(paymentId);
      } catch (error) {
        console.error('Error processing refund:', error);
      }
    }
  };

  const exportData = () => {
    // Implementation for exporting payment data
    const data = paymentHistory.map(payment => ({
      Date: format(new Date(payment.created_at), 'yyyy-MM-dd'),
      User: payment.user_email,
      Amount: `AED ${payment.amount}`,
      Status: payment.status,
      'Transaction ID': payment.transaction_id
    }));
    
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medtracker-payments-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Management</h2>
          <p className="text-gray-600 mt-1">Monitor subscriptions and revenue</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={exportData}
          className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-200"
        >
          <SafeIcon icon={FiDownload} />
          <span>Export Data</span>
        </motion.button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <p className={`text-sm mt-1 ${
                  stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change} from last month
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-100 flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className={`text-xl text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['overview', 'payments', 'trials'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                activeTab === tab
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
              <div className="flex items-center space-x-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">User</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Amount</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Date</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Status</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paymentHistory
                  ?.filter(payment => filterStatus === 'all' || payment.status === filterStatus)
                  ?.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <div className="font-medium text-gray-900">{payment.user_name}</div>
                          <div className="text-sm text-gray-500">{payment.user_email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900">
                        AED {payment.amount}
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {format(new Date(payment.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        {payment.status === 'completed' && (
                          <button
                            onClick={() => handleRefund(payment.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'trials' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Trial Users</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">User</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Started</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Days Left</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Status</th>
                  <th className="text-left py-3 px-6 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {trialUsers?.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      {format(new Date(user.trial_started), 'MMM d, yyyy')}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`font-medium ${
                        user.days_left > 5 ? 'text-green-600' : 
                        user.days_left > 0 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {user.days_left > 0 ? `${user.days_left} days` : 'Expired'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleExtendTrial(user.id, 7)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          +7 days
                        </button>
                        <button
                          onClick={() => handleExtendTrial(user.id, 14)}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          +14 days
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;